import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ShieldAlert,
  X,
  Copy,
  Globe,
  ExternalLink,
  ShieldCheck,
  Zap,
  User,
  History
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";

/**
 * DetailSidebarIOC
 * Handles specific technical details for Indicators of Compromise
 */
export default function DetailSidebarIOC({
  open,
  onClose,
  iocId,
  jwtToken,
  apiUrl = "https://whiteintel.io/api/regular/app/v2/threatfeed/ioc_feeds.php",
  licenseUrl = "https://whiteintel.io/api/regular/app/v2/account/subscription.php",
  size = "normal",
  panelZ = 500,
  onUpgradeClick
}) {
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");
  const [hasThreatFeedAccess, setHasThreatFeedAccess] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const abortRef = useRef(null);

  const canOpen = open && Boolean(iocId) && Boolean(jwtToken);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  // Handle Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset state on close or ID change
  useEffect(() => {
    if (!open) {
      setData(null);
      setErr("");
      setSubError("");
      setHasThreatFeedAccess(null);
    }
  }, [open, iocId]);

  // 1) Subscription Check
  useEffect(() => {
    if (!canOpen) return;
    setSubLoading(true);
    (async () => {
      try {
        const res = await fetch(licenseUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({}),
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }
        const json = await res.json();
        const allowed = extractAccess(json);
        setHasThreatFeedAccess(allowed);
      } catch (e) {
        setHasThreatFeedAccess(false);
        setSubError("Subscription verification failed");
      } finally {
        setSubLoading(false);
      }
    })();
  }, [canOpen, jwtToken, licenseUrl]);

  // 2) Fetch IOC Detail
  useEffect(() => {
    if (!canOpen || hasThreatFeedAccess !== true) return;

    setLoading(true);
    setErr("");
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ metric: "detail", id: iocId }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        setData(raw?.data ?? raw ?? null);
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message || "Failed to load IOC");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [canOpen, apiUrl, jwtToken, iocId, hasThreatFeedAccess]);

  const locked = hasThreatFeedAccess === false;
  const effectiveData = locked ? mockIoc(iocId) : data;

  // Determine if we are in a loading state (includes initialization, access check, and data fetch)
  const isLoadingState = 
    (hasThreatFeedAccess === null) || 
    loading || 
    (hasThreatFeedAccess === true && !data && !err);

  const meta = useMemo(() => {
    const v = (x) => (!x ? "—" : x);
    const d = effectiveData || {};
    return {
      id: d.id ?? iocId,
      value: v(d.ioc_value),
      type: v(d.ioc_type),
      threat: v(d.threat_type),
      malware: v(d.malware_printable),
      aliases: v(d.malware_alias),
      confidence: d.confidence_level ?? 0,
      first_seen: v(fmtDT(d.first_seen_utc)),
      last_seen: v(fmtDT(d.last_seen_utc)),
      reporter: v(d.reporter),
      reference: d.reference || "",
      tags: d.tags ? d.tags.split(',') : [],
      is_compromised: d.is_compromised
    };
  }, [effectiveData, iocId]);

  return (
    <>
      {open && createPortal(
        <div onClick={onClose} className="fixed inset-0 bg-black/60 transition-opacity z-[400]" />,
        document.body
      )}

      {open && createPortal(
        <aside
          style={{ zIndex: panelZ }}
          className={`fixed right-0 top-0 h-full w-full ${size === "wide" ? "max-w-4xl" : "max-w-2xl"}
                      border-l border-neutral-800 bg-[#000414] shadow-2xl transition-transform duration-300 translate-x-0`}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-neutral-800">
            <div>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-sky-400">
                <ShieldAlert className="h-3 w-3" />
                <span>Technical Indicator Intelligence</span>
              </div>
              <h2 className="mt-1 text-xl font-mono font-bold text-white break-all">
                {meta.value}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <X className="h-5 w-5 text-neutral-500" />
            </button>
          </div>

          <div className="px-6 pt-4">
            {subLoading && <div className="text-xs text-neutral-500 animate-pulse">Authenticating license...</div>}
            {locked && <UpgradeCallout onClick={onUpgradeClick} />}
          </div>

          <div className="overflow-y-auto p-6 h-[calc(100%-110px)] relative custom-scrollbar">
            {isLoadingState && !locked ? (
              <Loader />
            ) : err && !locked ? (
              <div className="text-red-400 text-sm">{err}</div>
            ) : (
              <div className={`space-y-8 ${locked ? "blur-md select-none pointer-events-none" : ""}`}>
                
                {/* Confidence Meter */}
                <Section title="Analysis Confidence">
                    <div className="flex items-center gap-4 bg-neutral-900/40 p-4 rounded-xl border border-white/5">
                        <div className="relative h-16 w-16 flex-shrink-0">
                            <svg className="h-full w-full" viewBox="0 0 36 36">
                                <path className="text-neutral-800" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                <path className="text-sky-500" strokeDasharray={`${meta.confidence}, 100`} strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                                {meta.confidence}%
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-bold text-white uppercase tracking-tight">Confidence Score</div>
                            <p className="text-xs text-neutral-500 mt-1">Based on recursive telemetry and reporter reputation.</p>
                        </div>
                    </div>
                </Section>

                <Section title="Detection Timeline">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-neutral-900/40 p-3 rounded-lg border border-white/5">
                        <div className="text-[10px] uppercase text-neutral-500 font-bold mb-1">First Seen</div>
                        <div className="text-sm text-neutral-200 flex items-center gap-2"><History size={14}/> {meta.first_seen}</div>
                    </div>
                    <div className="bg-neutral-900/40 p-3 rounded-lg border border-white/5">
                        <div className="text-[10px] uppercase text-neutral-500 font-bold mb-1">Last Active</div>
                        <div className="text-sm text-sky-400 flex items-center gap-2"><Zap size={14}/> {meta.last_seen}</div>
                    </div>
                  </div>
                </Section>

                <Section title="Classification">
                    <MetaGrid items={[
                        { label: "Indicator", value: meta.value }, // Added Indicator here
                        { label: "IOC Type", value: meta.type },
                        { label: "Threat Class", value: meta.threat },
                        { label: "Malware family", value: meta.malware },
                        { label: "Reporter", value: meta.reporter },
                        { label: "Compromised", value: meta.is_compromised ? "YES" : "NO" },
                    ]} />
                </Section>

                {meta.aliases !== "—" && (
                    <Section title="Malware Aliases">
                        <div className="flex flex-wrap gap-2">
                            {meta.aliases.split(',').map(a => (
                                <span key={a} className="px-2 py-1 bg-red-500/10 border border-red-500/20 text-red-200 text-[10px] font-bold rounded uppercase tracking-wider">{a.trim()}</span>
                            ))}
                        </div>
                    </Section>
                )}

                {meta.reference && (
                    <Section title="External References">
                        <a href={meta.reference} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/10 transition-colors">
                            <span className="text-sm text-sky-400 truncate mr-4">{meta.reference}</span>
                            <ExternalLink size={14} className="text-neutral-500"/>
                        </a>
                    </Section>
                )}
              </div>
            )}
          </div>
        </aside>,
        document.body
      )}
    </>
  );
}

/* ---------------- UI SUB-COMPONENTS ---------------- */

function UpgradeCallout({ onClick }) {
  return (
    <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0" />
        <div>
          <h4 className="text-sm font-bold text-amber-200">IOC Intelligence Locked</h4>
          <p className="text-xs text-amber-200/60 mt-1 leading-relaxed">
            Technical indicators, malware aliases, and historical timelines are reserved for Threat Intel subscribers.
          </p>
          <button onClick={() => onClick ? onClick() : window.location.href='/upgrades'} className="mt-3 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-amber-500/20 transition-all">
            Upgrade License
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
    return (
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        <h3 className="mb-3 text-[10px] font-black tracking-[0.2em] text-neutral-500 uppercase">
          {title}
        </h3>
        {children}
      </section>
    );
}

function MetaGrid({ items = [] }) {
    return (
      <dl className="grid grid-cols-2 gap-y-6 gap-x-4">
        {items.map(({ label, value }, idx) => (
          <div key={idx}>
            <dt className="text-[10px] uppercase font-bold text-neutral-500 tracking-wider mb-1">
              {label}
            </dt>
            <dd className="text-sm text-neutral-100 font-medium break-all">
              {value}
            </dd>
          </div>
        ))}
      </dl>
    );
}

function Loader() {
    return (
      <div className="space-y-6">
        <div className="h-20 bg-neutral-800/40 animate-pulse rounded-xl" />
        <div className="grid grid-cols-2 gap-4">
            <div className="h-12 bg-neutral-800/40 animate-pulse rounded-lg" />
            <div className="h-12 bg-neutral-800/40 animate-pulse rounded-lg" />
        </div>
        <div className="h-40 bg-neutral-800/40 animate-pulse rounded-xl" />
      </div>
    );
}

function extractAccess(json) {
    const v = json?.subscription?.threat_feed ?? json?.data?.threat_feed ?? json?.threat_feed;
    return v === 1 || v === true || v === "1";
}

function mockIoc(id) {
    return {
        ioc_value: "47.93.141.98",
        ioc_type: "ip:port",
        threat_type: "botnet_cc",
        malware_printable: "Cobalt Strike",
        confidence_level: 85,
        reporter: "DonPasci",
        first_seen_utc: "2026-01-10",
        last_seen_utc: "2026-01-12"
    };
}

function fmtDT(d) {
  if (!d) return null;
  return new Date(d).toISOString().split('T')[0];
}